/**
 * @name UserContext
 * @descrip     const { showError, showSuccess } = useAlert();
     const { setLoadingState } = useLoadingContext();
     const { usuariosRfid, pessoas, saldosAlunos, saldosFuncionarios, saldosResponsaveis, context } = useTotemServices();o de usuário adaptado para o projeto IS Cantina Totem Web
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/contexts/UserContext/UserContext.tsx
 */

import React, { createContext, useCallback, useState } from "react";
import { useAlert } from "../../hooks/useAlert";
import type { RestricaoDetalhada } from "../../services/supabase/entities/RestricoesAlimentaresService";
import { useTotemServices } from "../../services/supabase/entities/useTotemServices";
import { useLoadingContext } from "../LoadingContext/useLoadingContext";

// Interfaces específicas para o totem
export interface IAlunoTotem {
  id: number;
  nome: string;
  matricula: string;
  saldo: number;
  foto?: string;
  restricoes_alimentares?: string[];
  produtosBloqueadosIds?: number[];
  temRestricaoGeral?: boolean;
  ativo: boolean;
  organizacao_id: number;
  tipo_pessoa?: string; // 'Aluno', 'Responsável', 'Funcionário'
}

// Interface simplificada para o contexto do totem
export interface UserContextType {
  // Funcionalidades específicas do totem
  alunoAtual: IAlunoTotem | null;
  setAlunoAtual: React.Dispatch<React.SetStateAction<IAlunoTotem | null>>;
  identificarAluno: (
    identificador: string,
    tipo: "rfid" | "codigo",
  ) => Promise<IAlunoTotem | null>;
  limparIdentificacaoAluno: () => void;
  atualizarSaldoAluno: (novoSaldo: number) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { showError, showSuccess } = useAlert();
  const { setLoadingState } = useLoadingContext();
  const {
    usuariosRfid,
    pessoas,
    saldosAlunos,
    saldosFuncionarios,
    saldosResponsaveis,
    restricoes,
    context,
  } = useTotemServices();

  // ⚡ DEBUG - Verificar se os serviços estão disponíveis
  console.log("🔧 DEBUG - Serviços disponíveis:", {
    saldosAlunos: !!saldosAlunos,
    saldosFuncionarios: !!saldosFuncionarios,
    saldosResponsaveis: !!saldosResponsaveis,
    organizationId: context.organizationId,
    isReady: context.isReady,
  });

  // Estados específicos do totem
  const [alunoAtual, setAlunoAtual] = useState<IAlunoTotem | null>(null);

  // Função simplificada para identificar aluno
  const identificarAluno = useCallback(
    async (
      identificador: string,
      tipo: "rfid" | "codigo",
    ): Promise<IAlunoTotem | null> => {
      try {
        setLoadingState("global-loading", true);
        console.log("🔍 Iniciando identificação de aluno:", {
          tipo,
          identificador,
        });

        // Verificar se a organização está definida
        if (!context.organizationId) {
          console.log(
            "❌ Organização não identificada - operador deve estar logado",
          );
          showError(
            "Erro: Organização não identificada. Operador deve estar logado.",
          );
          return null;
        }

        console.log("🏢 Usando organização:", context.organizationId);

        let alunoData: any = null;

        switch (tipo) {
          case "rfid":
            // ⚡ DEBUG CRÍTICO - Valor que está sendo enviado ao serviço RFID
            console.log("🔧 DEBUG CRÍTICO - Valor para serviço RFID:", {
              identificador: identificador,
              tamanho: identificador.length,
              tipo: typeof identificador,
              charCodes: Array.from(identificador).map((char) => ({
                char,
                code: char.charCodeAt(0),
              })),
              timestamp: new Date().toISOString(),
            });

            // Usar serviço de RFID para autenticação
            console.log("🔍 Buscando por RFID:", identificador);
            const rfidResult = await usuariosRfid.autenticarRfid(identificador);
            console.log("📋 Resposta da busca por RFID:", rfidResult);

            if (
              rfidResult.success &&
              "pessoa" in rfidResult &&
              rfidResult.pessoa
            ) {
              alunoData = rfidResult.pessoa;
              console.log("✅ Pessoa encontrada por RFID:", alunoData);
            } else {
              console.log(
                "❌ Pessoa não encontrada por RFID:",
                rfidResult.message,
              );
              // Não mostra erro aqui - deixa a segunda tentativa acontecer
              return null;
            }
            break;
          case "codigo":
            // Buscar por matrícula (quando RFID já falhou)
            console.log("🔍 Buscando por matrícula:", identificador);
            let pessoaResponse = await pessoas.getByMatricula(identificador);

            console.log("📋 Resposta da busca por matrícula:", pessoaResponse);

            if (pessoaResponse.data && !pessoaResponse.error) {
              alunoData = pessoaResponse.data;
              console.log("✅ Pessoa encontrada por matrícula:", alunoData);
            } else {
              console.log(
                "❌ Pessoa não encontrada por matrícula:",
                pessoaResponse.error,
              );
              // Não mostra erro aqui - será tratado no hook
              return null;
            }
            break;
        }

        if (!alunoData) {
          console.log("❌ Nenhum dado de aluno encontrado");
          showError("Aluno não encontrado");
          return null;
        }

        // Buscar saldo real baseado no tipo de pessoa
        let saldoAtual = 0;
        let tipoPessoa = alunoData.tipo_pessoa?.nome || "Desconhecido";

        // ⚡ DEBUG - Verificar tipo de pessoa identificado
        console.log("🔍 DEBUG - Pessoa identificada:", {
          nome: alunoData.nome,
          tipoPessoa: tipoPessoa,
          id: alunoData.id,
        });

        try {
          console.log(
            "💰 Buscando saldo da pessoa ID:",
            alunoData.id,
            "Tipo:",
            tipoPessoa,
          );

          if (
            tipoPessoa === "Aluno" ||
            tipoPessoa?.toLowerCase().includes("aluno")
          ) {
            const saldoResponse = await saldosAlunos.getSaldoByAluno(
              alunoData.id,
            );
            console.log("📋 Resposta do saldo aluno:", saldoResponse);

            if (saldoResponse.data && !saldoResponse.error) {
              const saldoData = saldoResponse.data as any;
              saldoAtual = saldoData.saldo_disponivel || 0;
            }
          } else if (
            tipoPessoa?.toLowerCase().includes("funcionário") ||
            tipoPessoa?.toLowerCase().includes("funcionario")
          ) {
            console.log(
              "👷 Buscando saldo FUNCIONÁRIO para pessoa ID:",
              alunoData.id,
            );
            const saldoResponse =
              await saldosFuncionarios.getSaldoByFuncionario(alunoData.id);
            console.log("📋 Resposta do saldo funcionário:", saldoResponse);

            if (saldoResponse.data && !saldoResponse.error) {
              const saldoData = saldoResponse.data as any;
              saldoAtual = saldoData.saldo_atual || 0;
              console.log("👷 Saldo FUNCIONÁRIO encontrado:", saldoAtual);
            } else {
              console.log(
                "❌ Erro ou dados não encontrados para funcionário:",
                saldoResponse,
              );
            }
          } else if (
            tipoPessoa === "Responsável" ||
            tipoPessoa?.toLowerCase().includes("responsável") ||
            tipoPessoa?.toLowerCase().includes("responsavel")
          ) {
            const saldoResponse =
              await saldosResponsaveis.getSaldoByResponsavel(alunoData.id);
            console.log("📋 Resposta do saldo responsável:", saldoResponse);

            if (saldoResponse.data && !saldoResponse.error) {
              const saldoData = saldoResponse.data as any;
              saldoAtual = saldoData.saldo_atual || 0;
            }
          } else {
            console.log("❓ Tipo de pessoa não reconhecido:", tipoPessoa);
          }

          console.log(
            "✅ Saldo encontrado:",
            saldoAtual,
            "para tipo:",
            tipoPessoa,
          );
        } catch (error) {
          console.error("❌ Erro ao buscar saldo:", error);
          saldoAtual = 0; // Continua mesmo sem saldo
        }

        const alunoTotem: IAlunoTotem = {
          id: alunoData.id,
          nome: alunoData.nome || "",
          matricula: alunoData.matricula || alunoData.codigo_academico || "",
          saldo: saldoAtual,
          foto: undefined,
          restricoes_alimentares: [],
          produtosBloqueadosIds: [],
          temRestricaoGeral: false,
          ativo: alunoData.ativo || false,
          organizacao_id: alunoData.fk_id_organizacao || context.organizationId,
          tipo_pessoa: tipoPessoa,
        };

        // Carregar restrições alimentares do banco de dados
        try {
          const restricoesData: RestricaoDetalhada[] =
            await restricoes.obterPorAluno(alunoData.id);
          alunoTotem.restricoes_alimentares = restricoesData
            .map((r: RestricaoDetalhada) => r.observacoes)
            .filter((obs: string | undefined): obs is string => Boolean(obs));
          alunoTotem.temRestricaoGeral = restricoesData.some(
            (r: RestricaoDetalhada) => !r.fk_id_produto,
          );
          alunoTotem.produtosBloqueadosIds = restricoesData
            .filter((r: RestricaoDetalhada) => r.fk_id_produto != null)
            .map((r: RestricaoDetalhada) => r.fk_id_produto as number);
          console.log("🛡️ Restrições carregadas:", {
            gerais: alunoTotem.temRestricaoGeral,
            produtos: alunoTotem.produtosBloqueadosIds,
          });
        } catch {
          console.warn(
            "⚠️ Não foi possível carregar restrições alimentares do aluno",
          );
        }

        console.log("✅ Aluno identificado:", {
          id: alunoTotem.id,
          nome: alunoTotem.nome,
          saldo: alunoTotem.saldo,
          organizacao: alunoTotem.organizacao_id,
        });

        setAlunoAtual(alunoTotem);
        showSuccess(
          `Bem-vindo(a), ${alunoTotem.nome}! (${tipoPessoa}) Saldo: R$ ${alunoTotem.saldo.toFixed(2)}`,
        );
        return alunoTotem;
      } catch (error) {
        console.error("❌ Erro ao identificar aluno:", error);
        showError("Erro ao identificar aluno");
        return null;
      } finally {
        setLoadingState("global-loading", false);
      }
    },
    [
      setLoadingState,
      showError,
      showSuccess,
      usuariosRfid,
      pessoas,
      saldosAlunos,
      restricoes,
      context.organizationId,
    ],
  );

  // Função para limpar identificação do aluno
  const limparIdentificacaoAluno = useCallback(() => {
    setAlunoAtual(null);
  }, []);

  // Função para atualizar saldo do aluno
  const atualizarSaldoAluno = useCallback(
    (novoSaldo: number) => {
      if (alunoAtual) {
        setAlunoAtual((prev) => (prev ? { ...prev, saldo: novoSaldo } : null));
      }
    },
    [alunoAtual],
  );

  return (
    <UserContext.Provider
      value={{
        alunoAtual,
        setAlunoAtual,
        identificarAluno,
        limparIdentificacaoAluno,
        atualizarSaldoAluno,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
